import moment                from "moment";
import Customer              from "../../../domain/customer/Customer";
import CustomerBirthDate     from "../../../domain/customer/CustomerBirthDate";
import CustomerBirthday      from "../../../domain/customer/CustomerBirthday";
import CustomerBirthMonth    from "../../../domain/customer/CustomerBirthMonth";
import CustomerBirthYear     from "../../../domain/customer/CustomerBirthYear";
import CustomerContact       from "../../../domain/customer/CustomerContact";
import CustomerDetail        from "../../../domain/customer/CustomerDetail";
import CustomerId            from "../../../domain/customer/CustomerId";
import CustomerNumber        from "../../../domain/customer/CustomerNumber";
import Facility              from "../../../domain/facility/Facility";
import FacilityId            from "../../../domain/facility/FacilityId";
import FacilityName          from "../../../domain/facility/FacilityName";
import Address               from "../../../domain/fundamentals/address/Address";
import Building              from "../../../domain/fundamentals/address/Building";
import City                  from "../../../domain/fundamentals/address/City";
import PostalCode            from "../../../domain/fundamentals/address/PostalCode";
import Prefecture            from "../../../domain/fundamentals/address/Prefecture";
import Street                from "../../../domain/fundamentals/address/Street";
import Date                  from "../../../domain/fundamentals/date/Date";
import Time                  from "../../../domain/fundamentals/date/Time";
import TimePeriod            from "../../../domain/fundamentals/date/TimePeriod";
import Email                 from "../../../domain/fundamentals/email/Email";
import FetchStatusRepository from "../../../domain/fundamentals/fetch/FetchStatusRepository";
import NotifyRepository      from "../../../domain/fundamentals/message/notify/NotifyRepository";
import FirstName             from "../../../domain/fundamentals/name/FirstName";
import FirstNameKana         from "../../../domain/fundamentals/name/FirstNameKana";
import LastName              from "../../../domain/fundamentals/name/LastName";
import LastNameKana          from "../../../domain/fundamentals/name/LastNameKana";
import Name                  from "../../../domain/fundamentals/name/Name";
import Phone                 from "../../../domain/fundamentals/phone/Phone";
import CreatedAt             from "../../../domain/fundamentals/trail/CreatedAt";
import Trail                 from "../../../domain/fundamentals/trail/Trail";
import UpdatedAt             from "../../../domain/fundamentals/trail/UpdatedAt";
import Reservation           from "../../../domain/reservation/Reservation";
import ReservationDetail     from "../../../domain/reservation/ReservationDetail";
import ReservationId         from "../../../domain/reservation/ReservationId";
import ReservationRepository from "../../../domain/reservation/ReservationRepository";
import Reservations          from "../../../domain/reservation/Reservations";
import ReservationTimeLine   from "../../../domain/reservation/ReservationTimeLine";
import ReservationTimeLines  from "../../../domain/reservation/ReservationTimeLines";
import Staff                 from "../../../domain/staff/Staff";
import StaffId               from "../../../domain/staff/StaffId";
import StaffMemo             from "../../../domain/staff/StaffMemo";
import FetchStatusDataSource from "../../../infrastructure/datasource/common/fetch/FetchStatusDataSource";
import NotifyDataSource      from "../../../infrastructure/datasource/notify/NotifyDataSource";
import ReservationDataSource from "../../../infrastructure/datasource/reservation/ReservationDataSource";

class GetReservations {
  private readonly repository: ReservationRepository = new ReservationDataSource(this.dispatch);
  private readonly fetchStatusRepository: FetchStatusRepository = new FetchStatusDataSource(this.dispatch);
  private readonly notifyRepository: NotifyRepository = new NotifyDataSource(this.dispatch);

  constructor(private readonly dispatch) {
  }

  public async execute(date: Date) {
    this.fetchStatusRepository.start();
    try {
      const response = await this.repository.findBy(date);
      const reservations: Reservation[] = response.data.reservations.map(reservation => new Reservation(
        new ReservationId(reservation.id),
        new ReservationDetail(
          Date.ofText(reservation.detail.date),
          new TimePeriod(
            Time.ofText(reservation.detail.from),
            Time.ofText(reservation.detail.to),
          ),
        ),
        new Customer(
          new CustomerId(reservation.customer.id),
          new CustomerDetail(
            new CustomerNumber(reservation.customer.detail.number),
            new Name(
              new LastName(reservation.customer.detail.last_name),
              new FirstName(reservation.customer.detail.first_name),
              new LastNameKana(reservation.customer.detail.last_name_kana),
              new FirstNameKana(reservation.customer.detail.first_name_kana),
            ),
            reservation.customer.detail.gender_type,
          ),
          new CustomerBirthday(
            new CustomerBirthYear(reservation.customer.birthday.year),
            new CustomerBirthMonth(reservation.customer.birthday.month),
            new CustomerBirthDate(reservation.customer.birthday.date),
          ),
          new CustomerContact(
            new Phone(reservation.customer.contact.phone_number),
            new Email(reservation.customer.contact.email),
          ),
          new Address(
            new PostalCode(reservation.customer.address.postal_code),
            new Prefecture(reservation.customer.address.prefecture),
            new City(reservation.customer.address.city),
            new Street(reservation.customer.address.street),
            new Building(reservation.customer.address.building)
          ),
          new Trail(
            new CreatedAt(moment(reservation.customer.trail.created_at)),
            new UpdatedAt(moment(reservation.customer.trail.updated_at))
          ),
        ),
        new Staff(
          new StaffId(reservation.staff.id),
          new Name(
            new LastName(reservation.staff.last_name),
            new FirstName(reservation.staff.first_name),
            new LastNameKana(reservation.staff.last_name_kana),
            new FirstNameKana(reservation.staff.first_name_kana)
          ),
          reservation.staff.gender,
          new StaffMemo(reservation.staff.memo)
        ),
        new Facility(
          new FacilityId(reservation.facility.id),
          new FacilityName(reservation.facility.name),
        ),
      ));

      const timelines: ReservationTimeLine[] = response.data.time_lines.map(timeline => new ReservationTimeLine(timeline));
      this.repository.setReservations(new Reservations(reservations, new ReservationTimeLines(timelines)));
    } catch (e) {
      this.notifyRepository.fetchFailure();
    } finally {
      this.fetchStatusRepository.finish();
    }
  }
}

export default GetReservations;
